import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api';
import type { ModuleViewProps } from '../../shell/types';
import {
  accountingFile,
  commitRun,
  fetchAccounting,
  fetchDiscrepancies,
  fetchOutbound,
  fetchPlan,
  fetchRuns,
  fetchStatus,
  OPEN_ENTITIES,
  openExportFile,
  pullInboundNow,
  resolveDiscrepancy,
  resolveRow,
  resync,
  rollbackRun,
  startDryRun,
  validateRun,
  type AccountingExport,
  type BridgeStatus,
  type DiscrepancyRow,
  type OutboundRow,
  type PlanReport,
  type PlanRow,
  type RunRow,
} from './api';
import { downloadText } from '../../shell/download';
import { makeBridgeT, money, type BridgeKey, type BridgeTranslate } from './i18n';
import { ModulePage, Table, TextArea, TextInput, Pagination, usePagination } from '../../shell/kit';
import { useTab } from '../../shell/hooks';
import { useToast } from '../../shell/toast';
import { ErrorState, LoadingState } from '../../shell/kit';
import { toResourceError, type ResourceError } from '../../shell/hooks';

type TabKey = 'status' | 'discrepancies' | 'runs' | 'export';

const TABS: ReadonlyArray<{ readonly key: TabKey; readonly label: BridgeKey }> = [
  { key: 'status', label: 'tab.status' },
  { key: 'discrepancies', label: 'tab.discrepancies' },
  { key: 'runs', label: 'tab.runs' },
  { key: 'export', label: 'tab.export' },
];

const NL = String.fromCharCode(10);

const SAMPLE_BALANCES = [
  'source_ref,phone,amount,expires_at,accrued_at',
  'LEGACY-1001,+77015550101,2401,2026-12-31,2025-11-03',
  'LEGACY-1002,+77015550102,580,,',
].join(NL);

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface">
      <h3 className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">{title}</h3>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'warn' | 'bad' }) {
  const color = tone === 'bad' ? 'text-danger' : tone === 'warn' ? 'text-warn' : 'text-ink';
  return (
    <div className="rounded-card border border-line bg-surface p-4">
      <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function StatusTab({ t, onError }: { t: BridgeTranslate; onError: (m: string) => void }) {
  const [status, setStatus] = useState<BridgeStatus | undefined>(undefined);
  const [failure, setFailure] = useState<ResourceError | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setFailure(undefined);
    try {
      setStatus(await fetchStatus());
    } catch (err) {
      // Ветка ошибки обязана сама снять «Загрузка…»: раньше кассир открывал
      // недоступный раздел и видел вечную загрузку под сообщением об отказе.
      setFailure(toResourceError(err));
      onError(err instanceof ApiError ? err.message : String(err));
    }
  }, [onError]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (): Promise<void> => {
    setBusy(true);
    try {
      await resync();
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (failure) return <ErrorState error={failure} onRetry={() => void load()} />;
  if (!status) return <LoadingState label={t('common.loading')} />;

  return (
    <div className="space-y-4">
      <div
        className={`flex flex-wrap items-center gap-3 rounded border px-4 py-2 text-sm ${
          status.available
            ? 'border-ok-line bg-ok-bg text-ok'
            : 'border-danger-line bg-danger-bg text-danger'
        }`}
      >
        <span className="font-medium">
          {t('status.adapter')}: {status.adapter}
        </span>
        <span>{status.available ? t('status.available') : t('status.unavailable')}</span>
        {status.readOnly ? <span className="text-xs">({t('status.readOnly')})</span> : null}
        <span className="text-xs opacity-80">
          {t('status.lastOk')}:{' '}
          {status.lastOkAt ? new Date(status.lastOkAt).toLocaleString() : t('status.never')}
        </span>
      </div>

      {!status.configured ? (
        <p className="rounded border border-warn-line bg-warn-bg px-4 py-2 text-sm text-warn">
          {t('status.notConfigured')}
          {status.error ? ` — ${status.error}` : ''}
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        <Tile
          label={t('status.queue')}
          value={String(status.queueDepth)}
          {...(status.queueDepth > 0 ? { tone: 'warn' as const } : {})}
        />
        <Tile
          label={t('status.failed')}
          value={String(status.failed)}
          {...(status.failed > 0 ? { tone: 'bad' as const } : {})}
        />
        <Tile
          label={t('status.open')}
          value={String(status.openDiscrepancies)}
          {...(status.openDiscrepancies > 0 ? { tone: 'warn' as const } : {})}
        />
      </div>

      <button
        type="button"
        onClick={() => void run()}
        disabled={busy}
        className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
      >
        {t('status.resync')}
      </button>
      <p className="text-xs text-ink-muted">{t('status.hint')}</p>

      <OutboundPanel t={t} onError={onError} />

      <Panel title={t('inbound.title')}>
        <Table>
          <thead>
            <tr className="text-left text-ink-muted">
              <th className="pb-2 font-medium">{t('inbound.entity')}</th>
              <th className="pb-2 font-medium">{t('inbound.lastOk')}</th>
              <th className="pb-2 font-medium">{t('inbound.error')}</th>
            </tr>
          </thead>
          <tbody>
            {status.inbound.map((row) => (
              <tr key={row.entity} className="border-t border-line">
                <td className="py-2 text-ink">{row.entity}</td>
                <td className="py-2 text-ink-muted">
                  {row.lastOkAt ? new Date(row.lastOkAt).toLocaleString() : t('status.never')}
                </td>
                <td className="py-2 text-xs text-danger">{row.lastError ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <button
          type="button"
          disabled={busy || !status.configured}
          onClick={() =>
            void (async () => {
              setBusy(true);
              try {
                await pullInboundNow();
                await load();
              } catch (err) {
                onError(err instanceof ApiError ? err.message : String(err));
              } finally {
                setBusy(false);
              }
            })()
          }
          className="mt-3 inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint"
        >
          {t('inbound.pull')}
        </button>
      </Panel>

      <Panel title={t('status.truth')}>
        <Table>
          <tbody>
            {status.truth.map((row) => (
              <tr key={row.entity} className="border-b border-line last:border-0">
                <td className="py-2 text-ink">{row.entity}</td>
                <td className="py-2 text-right text-ink-muted">
                  {t(`status.owner.${row.owner}` as BridgeKey)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Panel>
    </div>
  );
}

/**
 * Что именно стоит в очереди. Числа на плитках выше отвечают «сколько», эта
 * панель — «что и почему»: без последней ошибки застрявшая запись неотличима
 * от очереди, которая просто не успела уйти.
 */
function OutboundPanel({
  t,
  onError,
}: {
  t: BridgeTranslate;
  onError: (m: string) => void;
}) {
  const [status, setStatus] = useState<string>('pending');
  const [rows, setRows] = useState<readonly OutboundRow[] | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setRows(null);
    try {
      setRows(await fetchOutbound(status || undefined));
    } catch (err) {
      setRows([]);
      onError(err instanceof ApiError ? err.message : String(err));
    }
  }, [status, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Panel title={t('outbound.title')}>
      <div className="mb-3 flex flex-wrap gap-2">
        {(['pending', 'failed', 'sent', ''] as const).map((value) => (
          <button
            key={value || 'all'}
            type="button"
            onClick={() => setStatus(value)}
            className={`rounded-control border px-2.5 py-1 text-xs transition ${
              status === value
                ? 'border-brand-600 bg-brand-50 text-brand-700'
                : 'border-line text-ink-muted hover:bg-sunken'
            }`}
          >
            {t(`outbound.status.${value || 'all'}` as BridgeKey)}
          </button>
        ))}
      </div>

      {rows === null ? (
        <p className="text-sm text-ink-muted">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('outbound.empty')}</p>
      ) : (
        <Table>
          <thead>
            <tr className="text-left text-ink-muted">
              <th className="pb-2 font-medium">{t('outbound.entity')}</th>
              <th className="pb-2 font-medium">{t('outbound.op')}</th>
              <th className="pb-2 text-right font-medium">{t('outbound.attempts')}</th>
              <th className="pb-2 font-medium">{t('outbound.error')}</th>
              <th className="pb-2 font-medium">{t('status.delivered')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-line align-top">
                <td className="py-2 text-ink">
                  {row.entity}
                  <div className="font-mono text-xs text-ink-muted">{row.entityId}</div>
                </td>
                <td className="py-2 text-ink-muted">{row.op}</td>
                <td
                  className={`py-2 text-right tabular-nums ${
                    row.attempts > 1 ? 'text-warn' : 'text-ink-muted'
                  }`}
                >
                  {row.attempts}
                </td>
                <td className="py-2 text-xs text-danger">{row.lastError ?? '—'}</td>
                <td className="py-2 font-mono text-xs text-ink-muted">{row.deliveryRef ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Panel>
  );
}

function DiscrepanciesTab({ t, onError }: { t: BridgeTranslate; onError: (m: string) => void }) {
  const [rows, setRows] = useState<readonly DiscrepancyRow[]>([]);
  // Расхождений при миграции бывают сотни.
  const paged = usePagination(rows);

  const load = useCallback(async (): Promise<void> => {
    try {
      setRows(await fetchDiscrepancies('open'));
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    }
  }, [onError]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Panel title={t('tab.discrepancies')}>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('discrepancy.none')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          <Table>
            <thead>
              <tr className="text-left text-ink-muted">
                <th className="pb-2 font-medium">{t('common.entity')}</th>
                <th className="pb-2 font-medium">{t('discrepancy.side')}</th>
                <th className="pb-2 font-medium">{t('discrepancy.kind')}</th>
                <th className="pb-2 font-medium">{t('common.created')}</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {paged.rows.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="py-2 text-ink">{row.entity}</td>
                  <td className="py-2 text-ink-muted">{row.side}</td>
                  <td className="py-2 text-ink-muted">{row.kind}</td>
                  <td className="py-2 text-ink-muted">
                    {new Date(row.createdAt).toLocaleString()}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        void resolveDiscrepancy(row.id)
                          .then(load)
                          .catch((err: unknown) =>
                            onError(err instanceof ApiError ? err.message : String(err)),
                          )
                      }
                      className="inline-flex h-7 items-center justify-center rounded-control border border-line-strong px-2.5 text-xs font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint"
                    >
                      {t('discrepancy.resolve')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination paged={paged} />
        </div>
      )}
    </Panel>
  );
}

type RunKind = RunRow['kind'];

const RUN_KINDS: readonly RunKind[] = ['customers', 'balances', 'passes', 'feedback', 'history', 'catalog'];

const SAMPLES: Readonly<Record<RunKind, string>> = {
  customers: [
    'source_ref,phone,name,plates,visits,first_visit_at',
    'LEGACY-1,+77015550101,Арман,123ABC02|777KZ02,14,01.03.2024',
    'LEGACY-2,,Без телефона,456DEF02,2,',
  ].join(NL),
  balances: SAMPLE_BALANCES,
  passes: ['source_ref,phone,washes_left,value,valid_to', 'PASS-1,+77015550101,4,12000,2026-12-31'].join(NL),
  feedback: ['source_ref,received_at,channel,subject,body', 'FB-1,2025-10-01,whatsapp,Качество,Остались разводы'].join(NL),
  history: [
    'type,source_ref,phone,plate,site,date,amount,method,parent_ref',
    'visit,V-1,+77015550101,123ABC02,MEGA,05.02.2025 10:15,,,',
    'order,O-1,+77015550101,,MEGA,05.02.2025 10:20,4500,,V-1',
    'payment,P-1,,,MEGA,05.02.2025 10:40,4500,cash,O-1',
  ].join(NL),
  catalog: ['code,name,price', 'WASH-1,Мойка кузова,3500', 'VAC-1,Пылесос салона,1500'].join(NL),
};

function QualityPanel({ t, run }: { t: BridgeTranslate; run: RunRow }) {
  const quality = run.report['quality'] as Record<string, number> | undefined;
  if (!quality) return null;
  const tiles: ReadonlyArray<readonly [BridgeKey, number]> = [
    ['quality.total', quality['total'] ?? 0],
    ['quality.withPhone', quality['withPhone'] ?? 0],
    ['quality.withPlate', quality['withPlate'] ?? 0],
    ['quality.withBoth', quality['withPhoneAndPlate'] ?? 0],
    ['quality.withHistory', quality['withHistory'] ?? 0],
    ['quality.withoutContact', quality['withoutContact'] ?? 0],
    ['quality.duplicates', quality['duplicatePhones'] ?? 0],
  ];
  return (
    <Panel title={t('quality.title')}>
      <div className="grid gap-3 sm:grid-cols-4">
        {tiles.map(([key, value]) => (
          <Tile key={key} label={t(key)} value={String(value)} />
        ))}
      </div>
    </Panel>
  );
}

function PlanPanel({
  t,
  run,
  onError,
  onChanged,
}: {
  t: BridgeTranslate;
  run: RunRow;
  onError: (m: string) => void;
  onChanged: () => Promise<void>;
}) {
  const [plan, setPlan] = useState<PlanReport | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { customerId: string; amount: string; comment: string }>>({});

  const load = useCallback(async (): Promise<void> => {
    try {
      setPlan(await fetchPlan(run.id));
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    }
  }, [run.id, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!plan) return <LoadingState label={t('common.loading')} />;
  const pending = plan.rows.filter((row) => row.disposition === 'needs_decision');
  const draft = (ref: string) => drafts[ref] ?? { customerId: '', amount: '', comment: '' };

  const act = async (row: PlanRow, action: 'assign' | 'write_off'): Promise<void> => {
    const d = draft(row.sourceRef);
    const amount = d.amount.trim() ? Math.round(Number(d.amount.replace(',', '.')) * 100) : undefined;
    try {
      await resolveRow({
        runId: run.id,
        sourceRef: row.sourceRef,
        action,
        ...(d.customerId.trim() ? { customerId: d.customerId.trim() } : {}),
        ...(amount && amount > 0 ? { amountMinor: amount } : {}),
        ...(d.comment.trim() ? { comment: d.comment.trim() } : {}),
      });
      await load();
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <Panel title={t('plan.title')}>
      <div className="grid gap-3 sm:grid-cols-5">
        <Tile label={t('plan.staged')} value={money(plan.summary.stagedTotalMinor)} />
        <Tile label={t('plan.transfer')} value={money(plan.summary.transferMinor)} />
        <Tile label={t('plan.createProfiles')} value={String(plan.summary.createProfiles)} />
        <Tile label={t('plan.writtenOff')} value={money(plan.summary.writtenOffMinor)} />
        <Tile
          label={t('plan.pending')}
          value={String(plan.summary.needsDecision)}
          {...(plan.summary.needsDecision > 0 ? { tone: 'bad' as const } : {})}
        />
      </div>
      {pending.length === 0 ? (
        <p className="mt-3 text-sm text-ok">{t('plan.none')}</p>
      ) : (
        <Table className="mt-3">
          <thead>
            <tr className="text-left text-ink-muted">
              <th className="pb-2 font-medium">source_ref</th>
              <th className="pb-2 font-medium">{t('plan.reason')}</th>
              <th className="pb-2 text-right font-medium">{t('plan.amount')}</th>
              <th className="pb-2 font-medium">{t('plan.customerId')}</th>
              <th className="pb-2 font-medium">{t('plan.amount')}</th>
              <th className="pb-2 font-medium">{t('plan.comment')}</th>
              <th className="pb-2" />
            </tr>
          </thead>
          <tbody>
            {pending.map((row) => (
              <tr key={row.sourceRef} className="border-t border-line align-top">
                <td className="py-2 font-mono text-xs text-ink">{row.sourceRef}</td>
                <td className="py-2 text-xs text-danger">
                  {row.reason ? t(`reason.${row.reason}` as BridgeKey) : '—'}
                </td>
                <td className="py-2 text-right tabular-nums">
                  {row.amountRaw && row.reason === 'amount_unparsed' ? row.amountRaw : money(row.amountMinor)}
                </td>
                {(['customerId', 'amount', 'comment'] as const).map((field) => (
                  <td key={field} className="py-2 pr-2">
                    <TextInput
                      value={draft(row.sourceRef)[field]}
                      onChange={(e) =>
                        setDrafts({ ...drafts, [row.sourceRef]: { ...draft(row.sourceRef), [field]: e.target.value } })
                      }
                      className="h-7 w-32 text-xs"
                    />
                  </td>
                ))}
                <td className="py-2">
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => void act(row, 'assign')}
                      className="inline-flex h-7 items-center rounded-control border border-line-strong px-2 text-xs text-ink hover:bg-sunken"
                    >
                      {t('plan.assign')}
                    </button>
                    <button
                      type="button"
                      onClick={() => void act(row, 'write_off')}
                      className="inline-flex h-7 items-center rounded-control border border-danger-line px-2 text-xs text-danger hover:bg-danger-bg"
                    >
                      {t('plan.writeOff')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Panel>
  );
}

function RunsTab({ t, onError }: { t: BridgeTranslate; onError: (m: string) => void }) {
  const [runs, setRuns] = useState<readonly RunRow[]>([]);
  const pagedRuns = usePagination(runs);
  const [kind, setKind] = useState<RunKind>('balances');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setRuns(await fetchRuns());
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    }
  }, [onError]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const current = runs.find((run) => run.id === selected) ?? null;

  return (
    <div className="space-y-4">
      <Panel title={t('run.dryRun')}>
        <div className="mb-2 flex flex-wrap gap-2">
          {RUN_KINDS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setKind(value)}
              className={`rounded-control border px-2.5 py-1 text-xs transition ${
                kind === value
                  ? 'border-brand-600 bg-brand-50 text-brand-700'
                  : 'border-line text-ink-muted hover:bg-sunken'
              }`}
            >
              {t(`run.kind.${value}` as BridgeKey)}
            </button>
          ))}
        </div>
        {/* Пробный прогон работает от приложенной выгрузки: без неё адаптер
            честно отвечает «источник недоступен». Пример и кнопка «подставить»
            избавляют от угадывания формата. */}
        <label className="block text-sm text-ink-muted">
          {t('run.source')}
          <TextArea
            value={source}
            onChange={(e) => setSource(e.target.value)}
            rows={5}
            placeholder={SAMPLES[kind].split(NL)[0]}
            className="mt-1 font-mono text-xs"
          />
        </label>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={busy || !source.trim()}
            onClick={() =>
              void act(async () => {
                const result = (await startDryRun({ kind, adapter: 'file', source: { [kind]: source } })) as {
                  runId?: string;
                };
                if (result.runId) setSelected(result.runId);
              })
            }
            className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
          >
            {t('run.dryRun')}
          </button>
          <button
            type="button"
            onClick={() => setSource(SAMPLES[kind])}
            className="inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint"
          >
            {t('run.sample')}
          </button>
          <span className="text-xs text-ink-muted">{t(`run.hint.${kind}` as BridgeKey)}</span>
        </div>
      </Panel>

      <Panel title={t('tab.runs')}>
        {runs.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('common.empty')}</p>
        ) : (
          <div className="flex flex-col gap-3">
            <Table>
              <thead>
                <tr className="text-left text-ink-muted">
                  <th className="pb-2 font-medium">{t('run.kind')}</th>
                  <th className="pb-2 font-medium">{t('common.status')}</th>
                  <th className="pb-2 text-right font-medium">{t('run.rows')}</th>
                  <th className="pb-2 text-right font-medium">{t('run.total')}</th>
                  <th className="pb-2 font-medium">{t('run.started')}</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {pagedRuns.rows.map((run) => (
                  <tr
                    key={run.id}
                    className={`border-t border-line ${run.id === selected ? 'bg-sunken' : ''}`}
                    onClick={() => setSelected(run.id)}
                  >
                    <td className="py-2 text-ink">{t(`run.kind.${run.kind}` as BridgeKey)}</td>
                    <td className="py-2 text-ink-muted">
                      {t(`run.status.${run.status}` as BridgeKey)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {run.checksumBefore?.rows ?? '—'}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {run.checksumBefore && run.kind === 'balances' ? money(run.checksumBefore.totalMinor) : '—'}
                    </td>
                    <td className="py-2 text-ink-muted">
                      {new Date(run.startedAt).toLocaleString()}
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {run.status === 'dry_run' || run.status === 'failed' ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void act(() => validateRun(run.id))}
                            className="inline-flex h-7 items-center justify-center rounded-control border border-line-strong px-2.5 text-xs font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint"
                          >
                            {t('run.validate')}
                          </button>
                        ) : null}
                        {run.status === 'validated' ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void act(() => commitRun(run.id))}
                            className="inline-flex h-7 items-center justify-center rounded-control bg-brand-600 px-2.5 text-xs font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
                          >
                            {t('run.commit')}
                          </button>
                        ) : null}
                        {run.status === 'committed' && run.kind === 'balances' ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void act(() => rollbackRun(run.id))}
                            className="inline-flex h-7 items-center justify-center rounded-control border border-danger-line px-2.5 text-xs font-medium text-danger transition hover:bg-danger-bg disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {t('run.rollback')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination paged={pagedRuns} />
          </div>
        )}
        <p className="mt-3 text-xs text-ink-muted">{t('run.commitWarning')}</p>
      </Panel>

      {current ? <QualityPanel t={t} run={current} /> : null}
      {current && current.kind === 'balances' && current.status !== 'draft' ? (
        <PlanPanel t={t} run={current} onError={onError} onChanged={load} />
      ) : null}
      {current ? (
        <Panel title={t('run.report')}>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-xs text-ink-muted">
            {JSON.stringify(current.report, null, 2)}
          </pre>
        </Panel>
      ) : null}
    </div>
  );
}

function ExportTab({ t, onError }: { t: BridgeTranslate; onError: (m: string) => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [data, setData] = useState<AccountingExport | undefined>(undefined);
  const [entity, setEntity] = useState<string>('customers');

  const range = (): { from: string; to: string } => ({
    from: new Date(from).toISOString(),
    to: new Date(`${to}T23:59:59`).toISOString(),
  });

  const build = async (): Promise<void> => {
    try {
      const r = range();
      setData(await fetchAccounting(r.from, r.to));
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const save = async (file: Promise<{ text: string; filename: string; type: string }>): Promise<void> => {
    try {
      const result = await file;
      downloadText(result.filename, result.text, result.type.split(';')[0] ?? 'text/plain');
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const button =
    'inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken';

  return (
    <div className="space-y-4">
      <Panel title={t('tab.export')}>
        <p className="mb-3 text-xs text-ink-muted">{t('export.byOperation')}</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-ink-muted">
            {t('export.from')}
            <TextInput
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 block"
            />
          </label>
          <label className="text-sm text-ink-muted">
            {t('export.to')}
            <TextInput
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 block"
            />
          </label>
          <button
            type="button"
            onClick={() => void build()}
            className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
          >
            {t('export.build')}
          </button>
          <button type="button" className={button} onClick={() => void save(accountingFile(range().from, range().to, 'csv'))}>
            {t('export.csv')}
          </button>
          <button type="button" className={button} onClick={() => void save(accountingFile(range().from, range().to, '1c-csv'))}>
            {t('export.onecCsv')}
          </button>
          <button
            type="button"
            className={button}
            onClick={() => void save(accountingFile(range().from, range().to, 'commerceml'))}
          >
            {t('export.commerceml')}
          </button>
        </div>

        {data ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Tile label={t('export.orders')} value={String(data.totals.orders)} />
            <Tile label={t('export.total')} value={money(data.totals.totalMinor)} />
            <Tile label={t('export.paid')} value={money(data.totals.paidMinor)} />
            <Tile label={t('export.refunded')} value={money(data.totals.refundedMinor)} />
            <Tile label={t('export.subscription')} value={money(data.totals.subscriptionFeesMinor)} />
            <Tile label={t('export.net')} value={money(data.totals.netMinor)} />
          </div>
        ) : null}
      </Panel>

      <Panel title={t('open.title')}>
        <p className="mb-3 text-xs text-ink-muted">{t('open.hint')}</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-ink-muted">
            {t('open.entity')}
            <select
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              className="mt-1 block h-8 rounded-control border border-line-strong bg-surface px-2 text-sm text-ink"
            >
              {OPEN_ENTITIES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className={button} onClick={() => void save(openExportFile(entity, 'csv'))}>
            CSV
          </button>
          <button type="button" className={button} onClick={() => void save(openExportFile(entity, 'json'))}>
            JSON
          </button>
        </div>
      </Panel>
    </div>
  );
}

export default function BridgeModule({ title, user }: ModuleViewProps) {
  const t = useMemo(() => makeBridgeT(user.locale), [user.locale]);
  const [tab, setTab] = useTab<TabKey>(
    'bridge',
    TABS.map((entry) => entry.key),
    'status',
  );
  const toast = useToast();

  // Ответ показывается рядом с действием, а не плашкой вверху страницы.
  const onError = useCallback(
    (message: string) => {
      if (message) toast.error(message);
    },
    [toast],
  );

  return (
    <ModulePage
      title={title}
      subtitle={t('module.subtitle')}
      tabs={TABS.map((entry) => ({ key: entry.key, label: t(entry.label) }))}
      activeTab={tab}
      onTab={setTab}
    >
      {tab === 'status' ? <StatusTab t={t} onError={onError} /> : null}
      {tab === 'discrepancies' ? <DiscrepanciesTab t={t} onError={onError} /> : null}
      {tab === 'runs' ? <RunsTab t={t} onError={onError} /> : null}
      {tab === 'export' ? <ExportTab t={t} onError={onError} /> : null}
    </ModulePage>
  );
}
